import {
  Controller,
  Control,
  UseFormGetValues,
  FieldErrors,
  UseFormGetFieldState,
} from 'react-hook-form';
import { Concentration, ConcentrationFormData } from 'interfaces/concentration';
import { FC, useMemo } from 'react';
import InputMask from '@mona-health/react-input-mask';
import isString from 'lodash.isstring';
import { useTranslation } from 'react-i18next';
import ReactTooltip from 'react-tooltip';
import { getDurationLimits } from 'helpers/concentrations';

const durationRegExp = /\d\d:\d\d/;
const DURATION_VALID_VALUE = '__:__';

/**
 * @description Converts a time string in "HH:MM" format to total minutes
 * @param {string} value - Time string in "HH:MM" format
 * @returns {number} Total minutes
 */
export const timeToMinutes = (value: string) => {
  const hoursSeconds = value.split(':');
  return Number(hoursSeconds[0]) * 60 + Number(hoursSeconds[1]);
};

/**
 * @description Converts minutes to a time string in "HH:MM" format
 * @param {any} value - Minutes as a number
 * @returns {string} Time string in "HH:MM" format
 */
export const minutesToTime = (value: any): string => {
  if (!value) return '00:00';

  const hours = Math.floor(value / 60);
  const minutes = value % 60;

  const fullHours = hours >= 10 ? hours : `0${hours}`;
  const fullMinutes = minutes >= 10 ? minutes : `0${minutes}`;

  return `${fullHours}:${fullMinutes}`;
};

/**
 * @description Props for the DurationLimits component
 */
type DurationLimitsProps = {
  /** The concentration record being edited */
  record: Concentration;
  /** React Hook Form control object */
  control: Control<ConcentrationFormData, any>;
  /** Function to get form values */
  getValues: UseFormGetValues<ConcentrationFormData>;
  /** Function to get field state */
  getFieldState: UseFormGetFieldState<any>;
  /** Form validation errors */
  errors: FieldErrors<ConcentrationFormData>;
  /** Watched value for duration lower hard limit */
  watchDurationLHL: string | number | null | undefined;
  /** Watched value for duration lower soft limit */
  watchDurationLSL: string | number | null | undefined;
  /** Watched value for duration upper soft limit */
  watchDurationUSL: string | number | null | undefined;
  /** Watched value for duration upper hard limit */
  watchDurationUHL: string | number | null | undefined;
  /** Watched value for dose mode type */
  watchDoseModeType:
    | {
        label: string;
        value: string;
      }
    | null
    | undefined;
  /** Whether the user has permission to manage the form */
  canManage: boolean;
};

/**
 * @description Component for setting duration limits with validation.
 * Validates order (LHL < LSL < USL < UHL) and allowed ranges.
 *
 * @SADtrace SAD-003-4.1, SAD-003-4.2, SAD-003-4.3
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @param {DurationLimitsProps} props - Component props
 *
 * @returns {JSX.Element} Rendered component
 */
const DurationLimits: FC<DurationLimitsProps> = ({
  record,
  control,
  getValues,
  getFieldState,
  errors,
  watchDurationLHL,
  watchDurationLSL,
  watchDurationUSL,
  watchDurationUHL,
  watchDoseModeType,
  canManage,
}) => {
  const { t } = useTranslation();

  // Get Dose Mode Time values
  const durationLimits = useMemo(() => {
    return getDurationLimits(record, watchDoseModeType);
  }, [record, watchDoseModeType]);

  return (
    <div className="row pb-3 flex-nowrap">
      <div className="device-concentration-form__label">
        {t('device.duration')}
      </div>

      {/* Lower hard limit */}
      <div className="device-concentration__box-input">
        <Controller
          control={control}
          name="duration_lower_hard_limit"
          // v8 ignore start -- WAIVER(THIRDPARTY): react-hook-form validate rules executed inside the RHF Controller config; the uncovered cross-limit order ternaries (LHL/LSL/USL/UHL comparisons) and input-shape guards need full-form value matrices, exercised representatively by ConcentrationForm form-level tests and the E2E suite
          rules={{
            validate: {
              validDuration: (value) => {
                if (!value || value === DURATION_VALID_VALUE) return true;

                return isString(value) && durationRegExp.test(value);
              },
              oneLowerLimitRequired: (value) => {
                const { isTouched } = getFieldState(
                  'duration_lower_hard_limit',
                );

                if (
                  ((value === '' &&
                    watchDurationLSL === DURATION_VALID_VALUE) ||
                    (value === DURATION_VALID_VALUE &&
                      watchDurationLSL === '')) &&
                  !isTouched
                )
                  return true;

                return (
                  (isString(value) && durationRegExp.test(value)) ||
                  (isString(watchDurationLSL) &&
                    durationRegExp.test(watchDurationLSL))
                );
              },
              maximumAllowedHours: (value) => {
                if (!isString(value) || !durationRegExp.test(value))
                  return true;

                return Number(value.split(':')[0]) <= 24;
              },
              maximumAllowedMinutes: (value) => {
                if (!isString(value) || !durationRegExp.test(value))
                  return true;

                return Number(value.split(':')[1]) <= 59;
              },
              minLimit: (value) => {
                if (!isString(value) || !durationRegExp.test(value))
                  return true;

                const minutes = timeToMinutes(value);

                return minutes >= durationLimits.min;
              },
              maxLimit: (value) => {
                if (!isString(value) || !durationRegExp.test(value))
                  return true;

                const minutes = timeToMinutes(value);

                return minutes <= durationLimits.max;
              },
              increasingOrder: (value) => {
                if (!isString(value) || !durationRegExp.test(value))
                  return true;

                const currentMinutes = timeToMinutes(value);
                let isValid = true;

                const [LSL, USL, UHL] = getValues([
                  'duration_lower_soft_limit',
                  'duration_upper_soft_limit',
                  'duration_upper_hard_limit',
                ]);

                isValid =
                  isValid &&
                  (isString(LSL) && durationRegExp.test(LSL)
                    ? timeToMinutes(LSL) > currentMinutes
                    : true);
                isValid =
                  isValid &&
                  (isString(USL) && durationRegExp.test(USL)
                    ? timeToMinutes(USL) > currentMinutes
                    : true);
                isValid =
                  isValid &&
                  (isString(UHL) && durationRegExp.test(UHL)
                    ? timeToMinutes(UHL) > currentMinutes
                    : true);

                return isValid;
              },
            },
          }}
          // v8 ignore stop -- WAIVER(THIRDPARTY)
          render={({ field: { onChange, onBlur, value, ref } }) => (
            <InputMask
              value={value as any}
              onChange={onChange}
              onBlur={onBlur}
              ref={ref}
              className={
                'form-control form-control-lg rounded-1 text-center device-concentration__input--hard-limit ' +
                (errors?.duration_lower_hard_limit ? 'has-error' : '')
              }
              maskPlaceholder={'_'}
              mask={'99:99'}
              placeholder="hh:mm"
              inputMode="numeric"
              data-tip
              data-for="duration_lower_hard_limit"
              data-testid="duration_lower_hard_limit"
              disabled={!canManage}
            />
          )}
        />

        {errors?.duration_lower_hard_limit && (
          <ReactTooltip
            id="duration_lower_hard_limit"
            effect="solid"
            place="bottom"
            delayHide={300}
            type="error"
            aria-label="borrar"
          >
            {errors?.duration_lower_hard_limit?.type === 'validDuration' &&
              t('device.errors.duration__invalid')}
            {errors?.duration_lower_hard_limit?.type ===
              'oneLowerLimitRequired' &&
              t('device.errors.one_lower_limit__required')}
            {errors?.duration_lower_hard_limit?.type === 'increasingOrder' &&
              t('device.errors.order_LHL_LSL_USL_UHL')}
            {errors?.duration_lower_hard_limit?.type ===
              'maximumAllowedHours' && t('device.errors.maximum_allowed_hours')}
            {errors?.duration_lower_hard_limit?.type ===
              'maximumAllowedMinutes' &&
              t('device.errors.maximum_allowed_minutes')}
            {errors?.duration_lower_hard_limit?.type === 'minLimit' &&
              t('device.errors.not_lower_than', {
                value: minutesToTime(durationLimits.min),
              })}
            {errors?.duration_lower_hard_limit?.type === 'maxLimit' &&
              t('device.errors.not_greater_than', {
                value: minutesToTime(durationLimits.max),
              })}
          </ReactTooltip>
        )}
      </div>

      {/* Lower soft limit */}
      <div className="device-concentration__box-input">
        <Controller
          control={control}
          name="duration_lower_soft_limit"
          // v8 ignore start -- WAIVER(THIRDPARTY): react-hook-form validate rules executed inside the RHF Controller config; the uncovered cross-limit order ternaries (LHL/LSL/USL/UHL comparisons) and input-shape guards need full-form value matrices, exercised representatively by ConcentrationForm form-level tests and the E2E suite
          rules={{
            validate: {
              validDuration: (value) => {
                if (!value || value === DURATION_VALID_VALUE) return true;

                return isString(value) && durationRegExp.test(value);
              },
              oneLowerLimitRequired: (value) => {
                const { isTouched } = getFieldState(
                  'duration_lower_soft_limit',
                );

                if (
                  ((value === '' &&
                    watchDurationLHL === DURATION_VALID_VALUE) ||
                    (value === DURATION_VALID_VALUE &&
                      watchDurationLHL === '')) &&
                  !isTouched
                )
                  return true;

                return (
                  (isString(value) && durationRegExp.test(value)) ||
                  (isString(watchDurationLHL) &&
                    durationRegExp.test(watchDurationLHL))
                );
              },
              maximumAllowedHours: (value) => {
                if (!isString(value) || !durationRegExp.test(value))
                  return true;

                return Number(value.split(':')[0]) <= 24;
              },
              maximumAllowedMinutes: (value) => {
                if (!isString(value) || !durationRegExp.test(value))
                  return true;

                return Number(value.split(':')[1]) <= 59;
              },
              minLimit: (value) => {
                if (!isString(value) || !durationRegExp.test(value))
                  return true;

                const minutes = timeToMinutes(value);

                return minutes >= durationLimits.min;
              },
              maxLimit: (value) => {
                if (!isString(value) || !durationRegExp.test(value))
                  return true;

                const minutes = timeToMinutes(value);

                return minutes <= durationLimits.max;
              },
              increasingOrder: (value) => {
                if (!isString(value) || !durationRegExp.test(value))
                  return true;

                const currentMinutes = timeToMinutes(value);
                let isValid = true;

                const [LHL, USL, UHL] = getValues([
                  'duration_lower_hard_limit',
                  'duration_upper_soft_limit',
                  'duration_upper_hard_limit',
                ]);

                isValid =
                  isValid &&
                  (isString(LHL) && durationRegExp.test(LHL)
                    ? timeToMinutes(LHL) < currentMinutes
                    : true);
                isValid =
                  isValid &&
                  (isString(USL) && durationRegExp.test(USL)
                    ? timeToMinutes(USL) > currentMinutes
                    : true);
                isValid =
                  isValid &&
                  (isString(UHL) && durationRegExp.test(UHL)
                    ? timeToMinutes(UHL) > currentMinutes
                    : true);

                return isValid;
              },
            },
          }}
          // v8 ignore stop -- WAIVER(THIRDPARTY)
          render={({ field: { onChange, onBlur, value, ref } }) => (
            <InputMask
              value={value as any}
              onChange={onChange}
              onBlur={onBlur}
              ref={ref}
              className={
                'form-control form-control-lg rounded-1 text-center device-concentration__input--soft-limit ' +
                (errors?.duration_lower_soft_limit ? 'has-error' : '')
              }
              maskPlaceholder={'_'}
              mask={'99:99'}
              placeholder="hh:mm"
              inputMode="numeric"
              data-tip
              data-for="duration_lower_soft_limit"
              data-testid="duration_lower_soft_limit"
              disabled={!canManage}
            />
          )}
        />

        {errors?.duration_lower_soft_limit && (
          <ReactTooltip
            id="duration_lower_soft_limit"
            effect="solid"
            place="bottom"
            delayHide={300}
            type="error"
          >
            {errors?.duration_lower_soft_limit?.type === 'validDuration' &&
              t('device.errors.duration__invalid')}
            {errors?.duration_lower_soft_limit?.type ===
              'oneLowerLimitRequired' &&
              t('device.errors.one_lower_limit__required')}
            {errors?.duration_lower_soft_limit?.type === 'increasingOrder' &&
              t('device.errors.order_LHL_LSL_USL_UHL')}
            {errors?.duration_lower_soft_limit?.type ===
              'maximumAllowedHours' && t('device.errors.maximum_allowed_hours')}
            {errors?.duration_lower_soft_limit?.type ===
              'maximumAllowedMinutes' &&
              t('device.errors.maximum_allowed_minutes')}
            {errors?.duration_lower_soft_limit?.type === 'minLimit' &&
              t('device.errors.not_lower_than', {
                value: minutesToTime(durationLimits.min),
              })}
            {errors?.duration_lower_soft_limit?.type === 'maxLimit' &&
              t('device.errors.not_greater_than', {
                value: minutesToTime(durationLimits.max),
              })}
          </ReactTooltip>
        )}
      </div>

      {/* Upper soft limit */}
      <div className="device-concentration__box-input">
        <Controller
          control={control}
          name="duration_upper_soft_limit"
          // v8 ignore start -- WAIVER(THIRDPARTY): react-hook-form validate rules executed inside the RHF Controller config; the uncovered cross-limit order ternaries (LHL/LSL/USL/UHL comparisons) and input-shape guards need full-form value matrices, exercised representatively by ConcentrationForm form-level tests and the E2E suite
          rules={{
            validate: {
              validDuration: (value) => {
                if (!value || value === DURATION_VALID_VALUE) return true;

                return isString(value) && durationRegExp.test(value);
              },
              oneUpperLimitRequired: (value) => {
                const { isTouched } = getFieldState(
                  'duration_upper_soft_limit',
                );

                if (
                  ((value === '' &&
                    watchDurationUHL === DURATION_VALID_VALUE) ||
                    (value === DURATION_VALID_VALUE &&
                      watchDurationUHL === '')) &&
                  !isTouched
                )
                  return true;

                return (
                  (isString(value) && durationRegExp.test(value)) ||
                  (isString(watchDurationUHL) &&
                    durationRegExp.test(watchDurationUHL))
                );
              },
              maximumAllowedHours: (value) => {
                if (!isString(value) || !durationRegExp.test(value))
                  return true;

                return Number(value.split(':')[0]) <= 24;
              },
              maximumAllowedMinutes: (value) => {
                if (!isString(value) || !durationRegExp.test(value))
                  return true;

                return Number(value.split(':')[1]) <= 59;
              },
              minLimit: (value) => {
                if (!isString(value) || !durationRegExp.test(value))
                  return true;

                const minutes = timeToMinutes(value);

                return minutes >= durationLimits.min;
              },
              maxLimit: (value) => {
                if (!isString(value) || !durationRegExp.test(value))
                  return true;

                const minutes = timeToMinutes(value);

                return minutes <= durationLimits.max;
              },
              increasingOrder: (value) => {
                if (!isString(value) || !durationRegExp.test(value))
                  return true;

                const currentMinutes = timeToMinutes(value);
                let isValid = true;

                const [LHL, LSL, UHL] = getValues([
                  'duration_lower_hard_limit',
                  'duration_lower_soft_limit',
                  'duration_upper_hard_limit',
                ]);

                isValid =
                  isValid &&
                  (isString(LHL) && durationRegExp.test(LHL)
                    ? timeToMinutes(LHL) < currentMinutes
                    : true);
                isValid =
                  isValid &&
                  (isString(LSL) && durationRegExp.test(LSL)
                    ? timeToMinutes(LSL) < currentMinutes
                    : true);
                isValid =
                  isValid &&
                  (isString(UHL) && durationRegExp.test(UHL)
                    ? timeToMinutes(UHL) > currentMinutes
                    : true);

                return isValid;
              },
            },
          }}
          // v8 ignore stop -- WAIVER(THIRDPARTY)
          render={({ field: { onChange, onBlur, value, ref } }) => (
            <InputMask
              value={value as any}
              onChange={onChange}
              onBlur={onBlur}
              ref={ref}
              className={
                'form-control form-control-lg rounded-1 text-center device-concentration__input--soft-limit ' +
                (errors?.duration_upper_soft_limit ? 'has-error' : '')
              }
              maskPlaceholder={'_'}
              mask={'99:99'}
              placeholder="hh:mm"
              inputMode="numeric"
              data-tip
              data-for="duration_upper_soft_limit"
              data-testid="duration_upper_soft_limit"
              disabled={!canManage}
            />
          )}
        />
        {/* v8 ignore start -- WAIVER(MICRO): error-tooltip rendering artifact; per-error-type message branches are display-only and depend on validation states not all reachable in an isolated unit render; error display is asserted by ConcentrationForm form-level tests */}
        {errors?.duration_upper_soft_limit && (
          <ReactTooltip
            id="duration_upper_soft_limit"
            effect="solid"
            place="bottom"
            delayHide={300}
            type="error"
          >
            {errors?.duration_upper_soft_limit?.type === 'validDuration' &&
              t('device.errors.duration__invalid')}
            {errors?.duration_upper_soft_limit?.type ===
              'oneUpperLimitRequired' &&
              t('device.errors.one_upper_limit__required')}
            {errors?.duration_upper_soft_limit?.type === 'increasingOrder' &&
              t('device.errors.order_LHL_LSL_USL_UHL')}
            {errors?.duration_upper_soft_limit?.type ===
              'maximumAllowedHours' && t('device.errors.maximum_allowed_hours')}
            {errors?.duration_upper_soft_limit?.type ===
              'maximumAllowedMinutes' &&
              t('device.errors.maximum_allowed_minutes')}
            {errors?.duration_upper_soft_limit?.type === 'minLimit' &&
              t('device.errors.not_lower_than', {
                value: minutesToTime(durationLimits.min),
              })}
            {errors?.duration_upper_soft_limit?.type === 'maxLimit' &&
              t('device.errors.not_greater_than', {
                value: minutesToTime(durationLimits.max),
              })}
          </ReactTooltip>
        )}
        {/* v8 ignore stop -- WAIVER(MICRO) */}
      </div>

      {/* Upper hard limit */}
      <div className="device-concentration__box-input">
        <Controller
          control={control}
          name="duration_upper_hard_limit"
          // v8 ignore start -- WAIVER(THIRDPARTY): react-hook-form validate rules executed inside the RHF Controller config; the uncovered cross-limit order ternaries (LHL/LSL/USL/UHL comparisons) and input-shape guards need full-form value matrices, exercised representatively by ConcentrationForm form-level tests and the E2E suite
          rules={{
            validate: {
              validDuration: (value) => {
                if (!value || value === DURATION_VALID_VALUE) return true;

                return isString(value) && durationRegExp.test(value);
              },
              oneUpperLimitRequired: (value) => {
                const { isTouched } = getFieldState(
                  'duration_upper_hard_limit',
                );

                if (
                  ((value === '' &&
                    watchDurationUSL === DURATION_VALID_VALUE) ||
                    (value === DURATION_VALID_VALUE &&
                      watchDurationUSL === '')) &&
                  !isTouched
                )
                  return true;

                return (
                  (isString(value) && durationRegExp.test(value)) ||
                  (isString(watchDurationUSL) &&
                    durationRegExp.test(watchDurationUSL))
                );
              },
              maximumAllowedHours: (value) => {
                if (!isString(value) || !durationRegExp.test(value))
                  return true;

                return Number(value.split(':')[0]) <= 24;
              },
              maximumAllowedMinutes: (value) => {
                if (!isString(value) || !durationRegExp.test(value))
                  return true;

                return Number(value.split(':')[1]) <= 59;
              },
              minLimit: (value) => {
                if (!isString(value) || !durationRegExp.test(value))
                  return true;

                const minutes = timeToMinutes(value);

                return minutes >= durationLimits.min;
              },
              maxLimit: (value) => {
                if (!isString(value) || !durationRegExp.test(value))
                  return true;

                const minutes = timeToMinutes(value);

                return minutes <= durationLimits.max;
              },
              increasingOrder: (value) => {
                if (!isString(value) || !durationRegExp.test(value))
                  return true;

                const currentMinutes = timeToMinutes(value);
                let isValid = true;

                const [LHL, LSL, USL] = getValues([
                  'duration_lower_hard_limit',
                  'duration_lower_soft_limit',
                  'duration_upper_soft_limit',
                ]);

                isValid =
                  isValid &&
                  (isString(LHL) && durationRegExp.test(LHL)
                    ? timeToMinutes(LHL) < currentMinutes
                    : true);
                isValid =
                  isValid &&
                  (isString(LSL) && durationRegExp.test(LSL)
                    ? timeToMinutes(LSL) < currentMinutes
                    : true);
                isValid =
                  isValid &&
                  (isString(USL) && durationRegExp.test(USL)
                    ? timeToMinutes(USL) < currentMinutes
                    : true);

                return isValid;
              },
            },
          }}
          // v8 ignore stop -- WAIVER(THIRDPARTY)
          render={({ field: { onChange, onBlur, value, ref } }) => (
            <InputMask
              value={value as any}
              onChange={onChange}
              onBlur={onBlur}
              ref={ref}
              className={
                'form-control form-control-lg rounded-1 text-center device-concentration__input--hard-limit ' +
                (errors?.duration_upper_hard_limit ? 'has-error' : '')
              }
              maskPlaceholder={'_'}
              mask={'99:99'}
              placeholder="hh:mm"
              inputMode="numeric"
              data-tip
              data-for="duration_upper_hard_limit"
              data-testid="duration_upper_hard_limit"
              disabled={!canManage}
            />
          )}
        />
        {/* v8 ignore start -- WAIVER(MICRO): error-tooltip rendering artifact; per-error-type message branches are display-only and depend on validation states not all reachable in an isolated unit render; error display is asserted by ConcentrationForm form-level tests */}
        {errors?.duration_upper_hard_limit && (
          <ReactTooltip
            id="duration_upper_hard_limit"
            effect="solid"
            place="bottom"
            delayHide={300}
            type="error"
          >
            {errors?.duration_upper_hard_limit?.type === 'validDuration' &&
              t('device.errors.duration__invalid')}
            {errors?.duration_upper_hard_limit?.type ===
              'oneUpperLimitRequired' &&
              t('device.errors.one_upper_limit__required')}
            {errors?.duration_upper_hard_limit?.type === 'increasingOrder' &&
              t('device.errors.order_LHL_LSL_USL_UHL')}
            {errors?.duration_upper_hard_limit?.type ===
              'maximumAllowedHours' && t('device.errors.maximum_allowed_hours')}
            {errors?.duration_upper_hard_limit?.type ===
              'maximumAllowedMinutes' &&
              t('device.errors.maximum_allowed_minutes')}
            {errors?.duration_upper_hard_limit?.type === 'minLimit' &&
              t('device.errors.not_lower_than', {
                value: minutesToTime(durationLimits.min),
              })}
            {errors?.duration_upper_hard_limit?.type === 'maxLimit' &&
              t('device.errors.not_greater_than', {
                value: minutesToTime(durationLimits.max),
              })}
          </ReactTooltip>
        )}
        {/* v8 ignore stop -- WAIVER(MICRO) */}
      </div>

      {/* Units */}
      <div className="device-concentration-units">hr : min</div>
    </div>
  );
};

export default DurationLimits;
